import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import NewsCard from "@/components/feature/NewsCard";
import { Breadcrumb } from "@/components/feature/PageHero";
import { useToast } from "@/components/base/Toast";
import { useSeo } from "@/hooks/useSeo";
import { getNewsById, getRelatedNews } from "@/mocks/news";
import { getTitleById } from "@/mocks/titles";

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const { notify } = useToast();
  const item = getNewsById(id);

  useSeo({
    title: item ? `${item.title} - 星光传媒` : "资讯详情 - 星光传媒",
    description: item
      ? `${item.summary} 星光传媒影视资讯，${item.publishedAt} 发布，栏目：${item.category}。`
      : "星光传媒影视资讯详情页。",
    keywords: item ? `${item.tags.join(",")},星光传媒,影视资讯` : "星光传媒,影视资讯",
    canonicalPath: `/news/detail/${id ?? ""}`,
  });

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  const related = useMemo(() => (item ? getRelatedNews(item, 3) : []), [item]);
  const linkedTitles = useMemo(
    () => (item ? item.relatedTitleIds.map((titleId) => getTitleById(titleId)).filter((t) => Boolean(t)) : []),
    [item],
  );

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: item?.title ?? "", url: window.location.href });
      } else {
        await navigator.clipboard.writeText(window.location.href);
      }
      notify("分享链接已复制，可发送给朋友");
    } catch {
      notify("已取消分享或浏览器不支持自动复制");
    }
  };

  if (!item) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar currentLabel="资讯" />
        <main className="flex-1 flex items-center justify-center px-4 pt-24 pb-16">
          <div className="text-center max-w-md">
            <span className="inline-flex w-16 h-16 rounded-full bg-background-100 text-foreground-400 items-center justify-center mb-5">
              <i className="ri-newspaper-line text-3xl w-8 h-8 flex items-center justify-center"></i>
            </span>
            <h1 className="font-heading text-2xl text-foreground-950 mb-2">这条资讯不存在</h1>
            <p className="text-sm text-foreground-500 mb-6">它可能已被归档或链接有误，返回资讯列表看看最新的消息吧。</p>
            <Link
              to="/news"
              className="h-10 px-5 inline-flex items-center rounded-md bg-primary-500 text-background-50 text-sm cursor-pointer whitespace-nowrap"
            >
              返回资讯列表
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar currentLabel="资讯" />
      <main>
        <section className="px-4 md:px-6 pt-24 md:pt-28 pb-6 border-b border-background-200">
          <div className="max-w-[1440px] mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: "影视资讯", to: "/news" },
                { label: item.category, to: `/news?cat=${encodeURIComponent(item.category)}` },
                { label: "详情" },
              ]}
            />
            <div className="mt-6 max-w-3xl">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="px-2 py-0.5 rounded text-[11px] bg-accent-100 text-accent-900 font-medium">{item.category}</span>
                {item.tags.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 rounded text-[11px] bg-background-100 text-foreground-600">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-950 leading-snug mb-4">
                {item.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-xs md:text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                  {item.publishedAt}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                  星光编辑部
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                  {(item.views / 10000).toFixed(1)} 万阅读
                </span>
                <button
                  type="button"
                  onClick={handleShare}
                  className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-background-300 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-forward-line w-3.5 h-3.5 flex items-center justify-center"></i>
                  分享
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-6 md:py-10">
          <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8 md:gap-10">
            <article className="max-w-3xl">
              <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200 mb-6">
                <img
                  src={item.cover}
                  alt={item.title}
                  title={`${item.title} 星光传媒影视资讯`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <p className="text-sm md:text-base text-foreground-800 leading-7 font-medium mb-6 pl-3 border-l-2 border-accent-400">
                {item.summary}
              </p>
              <div className="space-y-5">
                {item.paragraphs.map((paragraph, index) => {
                  if (paragraph.type === "quote") {
                    return (
                      <blockquote
                        key={`p-${index}`}
                        className="bg-background-100 rounded-lg px-4 md:px-6 py-4 md:py-5 text-sm md:text-base text-foreground-800 leading-relaxed"
                      >
                        <i className="ri-double-quotes-l text-accent-500 text-lg mr-1"></i>
                        {paragraph.content}
                      </blockquote>
                    );
                  }
                  if (paragraph.type === "image") {
                    return (
                      <figure key={`p-${index}`} className="my-6">
                        <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
                          <img
                            src={paragraph.content}
                            alt={paragraph.caption ?? item.title}
                            title={`${item.title} 配图`}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        {paragraph.caption && (
                          <figcaption className="text-xs text-foreground-400 mt-2 text-center">{paragraph.caption}</figcaption>
                        )}
                      </figure>
                    );
                  }
                  return (
                    <p key={`p-${index}`} className="text-sm md:text-base text-foreground-700 leading-8">
                      {paragraph.content}
                    </p>
                  );
                })}
              </div>

              <div className="mt-8 pt-5 border-t border-background-200 flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-400">标签：</span>
                {item.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 rounded-full bg-background-100 text-xs text-foreground-700">
                    #{tag}
                  </span>
                ))}
              </div>

              {linkedTitles.length > 0 && (
                <div className="mt-8">
                  <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-3">相关影视作品</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {linkedTitles.map(
                      (title) =>
                        title && (
                          <Link
                            key={title.id}
                            to={`/detail/${title.id}`}
                            className="group flex gap-3 bg-background-100 rounded-lg p-3 hover:bg-background-200/70 transition-colors cursor-pointer"
                          >
                            <div className="w-16 h-22 shrink-0 rounded overflow-hidden bg-background-200" style={{ height: 88 }}>
                              <img
                                src={title.poster}
                                alt={`《${title.name}》海报`}
                                title={`${title.name} 星光传媒`}
                                className="w-full h-full object-cover object-top"
                              />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground-950 group-hover:text-primary-700 transition-colors">
                                {title.name}
                              </p>
                              <p className="text-[11px] text-foreground-500 mt-1">
                                {title.genres.join(" · ")} · {title.year}
                              </p>
                              <p className="text-[11px] text-foreground-500 mt-1 line-clamp-2">{title.tagline}</p>
                            </div>
                          </Link>
                        ),
                    )}
                  </div>
                </div>
              )}

              <div className="mt-8 bg-accent-50 border border-accent-200 rounded-lg p-4">
                <p className="text-xs text-accent-900 leading-relaxed">
                  <i className="ri-information-line mr-1 w-3.5 h-3.5 inline-flex items-center justify-center"></i>
                  本文为虚构演示内容，人物、事件与数据均为示例，不代表真实报道。
                </p>
              </div>
            </article>

            <aside className="space-y-6">
              <div className="bg-background-100 rounded-lg p-4 md:p-5">
                <h2 className="text-sm font-medium text-foreground-950 mb-3">相关资讯</h2>
                {related.map((entry) => (
                  <NewsCard key={entry.id} item={entry} variant="horizontal" />
                ))}
              </div>
              <div className="bg-primary-950 rounded-lg p-4 md:p-5 text-background-200">
                <h2 className="text-sm font-medium text-background-50 mb-2">栏目导航</h2>
                <div className="flex flex-wrap gap-2">
                  {["新闻", "花絮", "独家采访"].map((cat) => (
                    <Link
                      key={cat}
                      to={`/news?cat=${encodeURIComponent(cat)}`}
                      className="px-2.5 py-1 rounded-full bg-background-50/10 text-xs text-background-200 hover:bg-background-50/20 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {cat}
                    </Link>
                  ))}
                </div>
                <Link
                  to="/news"
                  className="mt-4 inline-flex items-center gap-1 text-xs text-accent-300 hover:text-accent-200 cursor-pointer"
                >
                  返回资讯列表
                  <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center"></i>
                </Link>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}